(function () {

    "use strict";

    angular
        .module("harriApp")
        .service("businessDayUtils", BusinessDayUtils);

    BusinessDayUtils.$inject = ['TeamScheduleService'];

    function BusinessDayUtils (TeamScheduleService) {

        var self = this;

        var MINUTES = 60 * 1000;
        var HOURS = 60 * MINUTES;
        var DAY = 24 * HOURS;
        const DEFAULT_DATE_FORMAT = 'MMM DD, YYYY';

        /**
         * Adjusts open and close times to account for daylight saving time differences
         * @param {Date|string} date - The reference date for timezone offset calculation
         * @param {number} openTime - The open time in milliseconds
         * @param {number} closeTime - The close time in milliseconds
         * @returns {Array<number>} Array containing [adjustedOpenTime, adjustedCloseTime] in milliseconds
         */
        self.adjustOpenCloseTimeDaylightSaving = (date, openTime, closeTime) => {
            const dayOffset = new Date(date).getTimezoneOffset();
            const dayOpenOffset = new Date(openTime).getTimezoneOffset();
            const dayCloseOffset = new Date(closeTime).getTimezoneOffset();

            const adjustedOpenTime = openTime + (dayOpenOffset - dayOffset) * 60 * 1000;
            const adjustedCloseTime = closeTime + (dayCloseOffset - dayOffset) * 60 * 1000;

            return [adjustedOpenTime, adjustedCloseTime];
        }

        /**
         * Gets the business operation times for a specific day
         * @param {string} day - The day in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {Array<moment>} Array containing [startDate, endDate, now] as moment objects
         * @private
         */
        const getCurrentdayBusinessOperationTimes = (day, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const businessDay = TeamScheduleService.getBusinessDay(day);
            const startDate = timeZoneName ? moment.tz(day, dateFormat, timeZoneName) : moment(day, dateFormat);
            const endDate = startDate.clone();

            startDate.add(businessDay.open_time, 'milliseconds');
            endDate.add(businessDay.close_time, 'milliseconds');

            const now = timeZoneName ? Utils.getNowInBrandTimezoneByTimeZoneName(timeZoneName) : moment();

            return [startDate, endDate, now];
        }

        /**
         * Gets the business operation times for a week starting from the given date
         * @param {string} weekStartDate - The week start date in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {Array<moment>} Array containing [startDate, endDate, now] as moment objects
         * @private
         */
        const getWeekBusinessOperationTimes = (weekStartDate, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const businessDay = TeamScheduleService.getBusinessDay(weekStartDate);
            const startDate = timeZoneName ? moment.tz(weekStartDate, dateFormat, timeZoneName) : moment(weekStartDate, dateFormat);
            const endDate = startDate.clone().add(6, 'days');

            startDate.add(businessDay.open_time, 'milliseconds');
            endDate.add(businessDay.close_time, 'milliseconds');

            const now = timeZoneName ? Utils.getNowInBrandTimezoneByTimeZoneName(timeZoneName) : moment();

            return [startDate, endDate, now];
        }

        /**
         * Checks if a given day is in the past (business hours have ended)
         * @param {string} day - The day to check in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {boolean} True if the day is in the past
         */
        self.isPastDay = (day, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const [, endDate, now] = getCurrentdayBusinessOperationTimes(day, timeZoneName, dateFormat);

            const currentTimeAfterEndDate = endDate.isBefore(now);

            return currentTimeAfterEndDate;
        }

        /**
         * Checks if a given week is in the past (business hours have ended)
         * @param {string} weekStartDate - The week start date in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {boolean} True if the week is in the past
         */
        self.isPastWeek = (weekStartDate, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const [, endDate, now] = getWeekBusinessOperationTimes(weekStartDate, timeZoneName, dateFormat);

            const currentTimeAfterEndDate = endDate.isBefore(now);

            return currentTimeAfterEndDate;
        }

        /**
         * Checks if a given day is currently running (within business hours)
         * @param {string} day - The day to check in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {boolean} True if the day is currently running
         */
        self.isRunningDay = (day, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const [startDate, endDate, now] = getCurrentdayBusinessOperationTimes(day, timeZoneName, dateFormat);

            const currentTimeAfterStartDate = startDate.isBefore(now);
            const currentTimeBeforeEndDate = endDate.isAfter(now);

            return currentTimeAfterStartDate && currentTimeBeforeEndDate;
        }

        /**
         * Checks if a given week is currently running (within business hours)
         * @param {string} weekStartDate - The week start date in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {boolean} True if the week is currently running
         */
        self.isRunningWeek = (weekStartDate, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const [startDate, endDate, now] = getWeekBusinessOperationTimes(weekStartDate, timeZoneName, dateFormat);

            const currentTimeAfterStartDate = startDate.isBefore(now);
            const currentTimeBeforeEndDate = endDate.isAfter(now);

            return currentTimeAfterStartDate && currentTimeBeforeEndDate;
        }

        /**
         * Checks if a given week is in the future (business hours haven't started)
         * @param {string} weekStartDate - The week start date in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {boolean} True if the week is in the future
         */
        self.isFutureWeek = (weekStartDate, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const [startDate, , now] = getWeekBusinessOperationTimes(weekStartDate, timeZoneName, dateFormat);

            const currentTimeBeforeStartDate = startDate.isAfter(now);

            return currentTimeBeforeStartDate;
        }

        /**
         * Checks if a given day is in the future (business hours haven't started)
         * @param {string} day - The day to check in the specified date format
         * @param {string} timeZoneName - The timezone name (optional)
         * @param {string} dateFormat - The date format (default: 'MMM DD, YYYY')
         * @returns {boolean} True if the day is in the future
         */
        self.isFutureDay = (day, timeZoneName, dateFormat = DEFAULT_DATE_FORMAT) => {
            const [startDate, , now] = getCurrentdayBusinessOperationTimes(day, timeZoneName, dateFormat);

            const currentTimeBeforeStartDate = startDate.isAfter(now);

            return currentTimeBeforeStartDate;
        }

        /**
         * Gets the business day limits for the current running day in the brand's timezone
         * @param {string} timeZoneName - The timezone name for the brand
         * @returns {Object} Business day limits object with openTime and closeTime
         */
        self.getBrandTimezoneCurrentRunningDayLimits = (timeZoneName) => {
            const nowInBrandTimeZone = Utils.getNowInBrandTimezoneByTimeZoneName(timeZoneName);
            const currentDay = moment(nowInBrandTimeZone.format('YYYY-MM-DD'), 'YYYY-MM-DD');
            const previousDay = currentDay.clone().subtract(1, 'days');

            const businessDay = TeamScheduleService.getBusinessDay(currentDay);

            const currentDayLimits = self.getBusinessDayLimits(businessDay, currentDay);
            const previousDayLimits = self.getBusinessDayLimits(businessDay, previousDay);

            const now = moment(nowInBrandTimeZone.format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm');

            if (now.isBefore(currentDayLimits.openTime)) {
                return previousDayLimits;
            }

            return currentDayLimits;
        }

        self.normalizeTimeIntervals = function (intervals, openTime) {
            var convertedIntervals = [];

            var lastProcessedInterval;

            for (var i in intervals) {
                var interval = intervals[i];

                if (interval.start_time && interval.end_time) {
                    var referencePoint = lastProcessedInterval? (lastProcessedInterval.end_time + 1): openTime;
                    var startTimeDate = moment(interval.start_time);
                    var startTime = startTimeDate.hours() * HOURS + startTimeDate.minutes() * MINUTES;

                    var endTimeDate = moment(interval.end_time);
                    var endTime = endTimeDate.hours() * HOURS + endTimeDate.minutes() * MINUTES;

                    if (startTime < referencePoint) {
                        startTime += DAY;

                        if (startTime < referencePoint) {
                            startTime += DAY;
                        }
                    }

                    if (endTime <= referencePoint) {
                        endTime += DAY;

                        if (endTime <= referencePoint) {
                            endTime += DAY;
                        }
                    }

                    if (endTime <= startTime) {
                        endTime += DAY;

                        if (endTime <= startTime) {
                            endTime += DAY;
                        }
                    }

                    lastProcessedInterval = {
                        start_time: startTime,
                        end_time: endTime
                    };

                    convertedIntervals.push(lastProcessedInterval);
                }
                else {
                    console.error("Interval ignored, should have 'start_time' and 'end_time':", interval);
                }
            }

            return convertedIntervals;
        };

        var checkIntervalBusinessDayIntersection = function (businessDay, timeIntervals, date, normalizedInterval, checkStartTimeOnly) {

            if (typeof (timeIntervals) === 'object' && !angular.isArray(timeIntervals)) {
                timeIntervals = [timeIntervals];
            }

            if (timeIntervals.length === 0) {
                console.error("Intervals are empty, considered not in business day");
                return {};
            }

            var result = {};
            var closeTimeExtension = businessDay['close_time_extension'];

            var openTime = businessDay.open_time;
            var closeTime = businessDay.close_time + closeTimeExtension;

            var intervals = normalizedInterval? timeIntervals: self.normalizeTimeIntervals(timeIntervals, openTime);

            var startOfStart = intervals[0].start_time;
            var endOfEnd = intervals[intervals.length - 1].end_time;

            if (checkStartTimeOnly) {
                result['within'] = startOfStart >= openTime;
            } else {
                result['within'] = (startOfStart >= openTime && endOfEnd <= closeTime && startOfStart < endOfEnd);
            }
            result['in_extension'] = (closeTimeExtension && endOfEnd > businessDay.close_time && endOfEnd <= closeTime);

            return result;
        };

        self.getActualBusinessDay = function (date) {
            var businessDay = TeamScheduleService.getBusinessDay(date);
            var originalBusinessDay = businessDay.business_day;
            var closeTimeWithExtension = businessDay.close_time + businessDay.close_time_extension;
            var openTime = businessDay.open_time;
            var businessDayLength = (closeTimeWithExtension - openTime) / 3600 / 1000;

            if (businessDayLength >= 24) {
               return {
                   open_time: openTime - (30 * MINUTES),
                   close_time: businessDay.close_time,
                   close_time_extension: businessDay.close_time_extension + (30 * MINUTES)
               };
            } else {
                if (originalBusinessDay.close_time > originalBusinessDay.open_time) {
                    return {
                        open_time: 0,
                        close_time: DAY,
                        close_time_extension: businessDay.close_time_extension
                    };
                } else {
                    return {
                        open_time: closeTimeWithExtension - DAY,
                        close_time: businessDay.close_time,
                        close_time_extension: businessDay.close_time_extension
                    };
                }
            }
        };

        self.getBusinessDayIntersection = (timeIntervals, date, normalizedInterval, flexibleCloseTime, checkStartTimeOnly, businessDay) => {
            if (!businessDay) {
                businessDay = flexibleCloseTime ? self.getActualBusinessDay(date) : TeamScheduleService.getBusinessDay(date);
            }
            return checkIntervalBusinessDayIntersection(businessDay, timeIntervals, date, normalizedInterval, checkStartTimeOnly);
        }

        self.isWithinBusinessDay = function(timeIntervals, date, normalizedInterval, flexibleCloseTime, checkStartTimeOnly, businessDay) {
            if (!businessDay) {
                businessDay = flexibleCloseTime ? self.getActualBusinessDay(date) : TeamScheduleService.getBusinessDay(date);
            }
            return checkIntervalBusinessDayIntersection(businessDay, timeIntervals, date, normalizedInterval, checkStartTimeOnly)['within'];
        };

        self.isInBusinessDayExtension = function (timeIntervals, date, normalizedInterval, flexibleCloseTime, checkStartTimeOnly) {
            var businessDay = flexibleCloseTime ? self.getActualBusinessDay(date) : TeamScheduleService.getBusinessDay(date);
            return checkIntervalBusinessDayIntersection(businessDay, timeIntervals, date, normalizedInterval, checkStartTimeOnly)['in_extension'];
        };

        self.isTimeInBusinessDayExtension = (firstTime, secondTime, date, checkSecondTime, referenceTime) => {
            const businessDay = TeamScheduleService.getBusinessDay(date);
            const closeTimeExtension = businessDay['close_time_extension'];
            const openTime = businessDay.open_time;
            const closeTime = businessDay.close_time;
            const closeTimeWithExtension = closeTime + closeTimeExtension;
            let referenceTimeInMilliseconds = referenceTime?.hours() * HOURS + referenceTime?.minutes() * MINUTES;

            if (referenceTime && referenceTimeInMilliseconds < openTime) {
                referenceTimeInMilliseconds += DAY;
            }

            const intervals = self.normalizeTimeIntervals(
                [{start_time: firstTime, end_time: secondTime}],
                referenceTime? referenceTimeInMilliseconds: openTime
            );

            let startOfStart = intervals[0].start_time;
            let endOfEnd = intervals[intervals.length - 1].end_time;
            let timeToCompare = checkSecondTime? endOfEnd: startOfStart;

            return (timeToCompare > closeTime && timeToCompare <= closeTimeWithExtension)
        };

        self.checkTimeZoneOffsetDifference = (clockIn, clockOut, timezoneName) => {
            const clockInOffset = moment.tz(moment(clockIn.slice(0, -1)).format('YYYY-MM-DD HH:mm'), timezoneName)._offset;
            const clockOutOffset = moment.tz(moment(clockOut.slice(0, -1)).format('YYYY-MM-DD HH:mm'), timezoneName)._offset;
            return clockOutOffset !== clockInOffset;
        };

        self.isBusinessDayClosed = (day, brand) => {
            const clockDateActualBusinessDay = self.getActualBusinessDay(day);
            const clockDateActualBusinessDayLimits = self.getBusinessDayLimits(clockDateActualBusinessDay, day);
            return Utils.getNowInBrandTimezoneByTimeZoneName(brand.timezoneName).utcOffset(0, true).isAfter(clockDateActualBusinessDayLimits.closeTime.utcOffset(0, true))
        }

        self.getBusinessDayLimits = function (businessDay, day) {
            var openTime;
            var closeTime;
            var closeTimeWithoutExtension;
            if (day) {
                var dayTime = moment(day);
                openTime = dayTime.clone();
                closeTime = dayTime.clone();
                closeTimeWithoutExtension = dayTime.clone();
            } else {
                openTime = moment();
                closeTime = moment();
                closeTimeWithoutExtension = moment();
            }

            var extension = businessDay.close_time_extension / 1000;

            var openTimeSeconds = businessDay.open_time / 1000;

            openTime.hours(parseInt((openTimeSeconds) / 3600));
            openTime.minutes(parseInt(((openTimeSeconds) % 3600) / 60));

            var closeTimeSeconds = businessDay.close_time / 1000;

            closeTime.hours(parseInt((closeTimeSeconds + extension) / 3600));
            closeTime.minutes(parseInt(((closeTimeSeconds + extension) % 3600) / 60));

            closeTimeWithoutExtension.hours(parseInt((closeTimeSeconds) / 3600));
            closeTimeWithoutExtension.minutes(parseInt(((closeTimeSeconds) % 3600) / 60));

            return {
                closeTime: closeTime,
                openTime: openTime,
                closeTimeWithoutExtension: closeTimeWithoutExtension
            };
        };

        self.getIntervalBusinessDay = (startTime, businessDayOpenTime) => {
            let startDate = new Date(startTime);
            const startHour = startDate.getHours() * HOURS + startDate.getMinutes() * MINUTES;

            if (startHour < businessDayOpenTime) {
                startDate = startDate.getTime() - (24 * HOURS);
            }

            return moment(startDate);
        };
    }
})();